import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, FileText, Loader2, Download, ExternalLink } from 'lucide-react';
import {
  getExpenseReceiptItems,
  openExpenseReceipt,
  loadExpenseReceiptObjectUrl,
  receiptNeedsAuthFetch,
} from '@/utils/expenseDisplay';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

/** Thumbnail that loads /api receipt URLs with the admin token (plain <img src> would 401). */
function ReceiptThumb({ url, alt, onClick }) {
  const needsAuth = receiptNeedsAuthFetch(url);
  const [src, setSrc] = useState(needsAuth ? '' : url);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!needsAuth) {
      setSrc(url);
      return undefined;
    }
    let revoke = null;
    let cancelled = false;
    loadExpenseReceiptObjectUrl(url)
      .then((res) => {
        if (cancelled) {
          res.revoke();
          return;
        }
        revoke = res.revoke;
        setSrc(res.objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (revoke) revoke();
    };
  }, [url, needsAuth]);

  if (failed || !src) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex items-center justify-center w-full h-32 text-gray-400"
      >
        {failed ? <Image className="w-8 h-8" /> : <Loader2 className="w-6 h-6 animate-spin" />}
      </button>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-32 object-cover cursor-pointer hover:opacity-80"
      onClick={onClick}
    />
  );
}

export default function ExpenseReportReceipts({ report, className = '' }) {
  const { t } = useTranslation();
  const items = getExpenseReceiptItems(report);
  const [openingIdx, setOpeningIdx] = useState(null);
  const [preview, setPreview] = useState(null); // { idx, objectUrl, isPdf, revoke }

  useEffect(
    () => () => {
      if (preview?.revoke) preview.revoke();
    },
    [preview],
  );

  if (!items.length) return null;

  const openFailed = () =>
    toast.error(
      t('admin_expense_receipt_open_failed', {
        defaultValue: 'No se pudo abrir el ticket',
      }),
    );

  const handlePreview = async (item, idx) => {
    setOpeningIdx(idx);
    try {
      let loaded;
      try {
        loaded = await loadExpenseReceiptObjectUrl(item.viewUrl || item.url);
      } catch (err) {
        if (!item.viewUrl) throw err;
        loaded = await loadExpenseReceiptObjectUrl(item.url);
      }
      const { objectUrl, blob, revoke } = loaded;
      setPreview({
        idx,
        item,
        objectUrl,
        revoke,
        isPdf: item.isPdf || blob.type === 'application/pdf',
      });
    } catch (err) {
      console.error('Receipt preview failed:', err);
      openFailed();
    } finally {
      setOpeningIdx(null);
    }
  };

  const handleDownload = async (item, idx) => {
    const ok = await openExpenseReceipt(item.url, {
      forceDownload: true,
      downloadName: `ticket-${idx + 1}.${item.isPdf ? 'pdf' : 'jpg'}`,
    });
    if (!ok) openFailed();
  };

  return (
    <div className={`p-3 bg-gray-50 rounded border ${className}`.trim()}>
      <h4 className="font-semibold text-sm mb-2 flex items-center gap-1">
        <Image className="w-4 h-4" />
        {t('admin_tickets_receipts', { count: items.length })}
      </h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {items.map((item, idx) => (
          <div
            key={item.id || idx}
            className="border rounded overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
          >
            {item.isPdf ? (
              <div className="flex flex-col items-center justify-center h-32 w-full p-2 gap-1">
                <button
                  type="button"
                  onClick={() => handlePreview(item, idx)}
                  disabled={openingIdx === idx}
                  className="flex flex-col items-center justify-center flex-1 w-full text-blue-700 hover:bg-blue-50 disabled:opacity-60 rounded"
                  data-testid={`expense-receipt-pdf-${idx}`}
                >
                  {openingIdx === idx ? (
                    <Loader2 className="w-10 h-10 mb-1 animate-spin" />
                  ) : (
                    <FileText className="w-10 h-10 mb-1" />
                  )}
                  <span className="text-xs font-medium text-center">
                    {t('admin_expense_receipt_pdf')}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => handleDownload(item, idx)}
                  className="text-[10px] text-blue-600 underline"
                >
                  {t('admin_expense_receipt_download', { defaultValue: 'Descargar' })}
                </button>
              </div>
            ) : (
              <ReceiptThumb
                url={item.url}
                alt={t('admin_ticket_label', { n: idx + 1 })}
                onClick={() => handlePreview(item, idx)}
              />
            )}
            <div className="p-1 text-center text-[10px] text-gray-600">
              {t('admin_ticket_label')} {idx + 1}
              {item.type ? ` · ${item.type}` : ''}
            </div>
          </div>
        ))}
      </div>

      <Dialog
        open={Boolean(preview)}
        onOpenChange={(open) => {
          if (!open) setPreview(null);
        }}
      >
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-4">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2 pr-8">
              <span>
                {t('admin_ticket_label')} {preview ? preview.idx + 1 : ''}
              </span>
              {preview && (
                <span className="flex items-center gap-3 text-sm font-normal">
                  <a
                    href={preview.objectUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {t('admin_expense_receipt_new_tab', { defaultValue: 'Abrir en pestaña' })}
                  </a>
                  <button
                    type="button"
                    onClick={() => handleDownload(preview.item, preview.idx)}
                    className="flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    <Download className="w-4 h-4" />
                    {t('admin_expense_receipt_download', { defaultValue: 'Descargar' })}
                  </button>
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {preview &&
            (preview.isPdf ? (
              <iframe
                src={preview.objectUrl}
                title={`ticket-${preview.idx + 1}`}
                className="flex-1 w-full rounded border bg-white"
              />
            ) : (
              <div className="flex-1 overflow-auto rounded border bg-gray-100 flex items-start justify-center">
                <img
                  src={preview.objectUrl}
                  alt={t('admin_ticket_label', { n: preview.idx + 1 })}
                  className="max-w-full h-auto"
                />
              </div>
            ))}
        </DialogContent>
      </Dialog>
    </div>
  );
}
